import { faGithub, faLinkedin, faMedium, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { faCode, faExternalLinkAlt, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React, { useEffect, useRef, useState } from 'react';
import ContactWidget from '../components/ContactWidget';
import Footer from '../components/Footer';
import Navbar from '../components/navBar';
import SocialLink from '../components/SocialLink';
import projectsData from '../data/projects.json';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

export default function Projects() {
    const [filter, setFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProject, setSelectedProject] = useState(null);
    
    // Refs for animations
    const heroRef = useRef(null);
    const heroContentRef = useRef(null);
    const filtersRef = useRef(null);
    const filterButtonsRef = useRef([]);
    const searchRef = useRef(null);
    const projectsGridRef = useRef(null);
    const projectCardsRef = useRef([]);
    const modalRef = useRef(null);
    
    // Filter projects based on category and search query
    const filteredProjects = projectsData.filter(project => {
        const matchesCategory = filter === 'all' || project.category === filter;
        const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            project.tech.some(tech => tech.toLowerCase().includes(searchQuery.toLowerCase()));
        
        return matchesCategory && matchesSearch;
    });

    // Handle modal open/close
    const openModal = (project) => {
        setSelectedProject(project);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setSelectedProject(null);
        document.body.style.overflow = 'unset';
    };

    // Close modal with escape key
    useEffect(() => {
        const handleEsc = (event) => {
            if (event.keyCode === 27) closeModal();
        };
        window.addEventListener('keydown', handleEsc);
        
        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'unset';
        };
    }, []);

    // GSAP Animations
    useEffect(() => {
        // Reset refs arrays
        filterButtonsRef.current = [];
        projectCardsRef.current = [];
        
        // Hero section animation
        gsap.fromTo(
            heroRef.current,
            { opacity: 0, scale: 0.95 },
            { 
                opacity: 1, 
                scale: 1,
                duration: 1.2, 
                ease: "power3.out",
            }
        );
        
        // Hero content animation
        gsap.fromTo(
            heroContentRef.current,
            { y: 30, opacity: 0 },
            { 
                y: 0, 
                opacity: 1, 
                duration: 1.2, 
                delay: 0.3,
                ease: "back.out(1.7)" 
            }
        );
        
        // Filters container animation
        gsap.fromTo(
            filtersRef.current,
            { y: 30, opacity: 0 },
            { 
                y: 0, 
                opacity: 1, 
                duration: 0.8, 
                delay: 0.5,
                ease: "power2.out" 
            }
        );
        
        // Filter buttons staggered animation
        gsap.fromTo(
            filterButtonsRef.current,
            { 
                scale: 0.9, 
                opacity: 0,
                y: 10
            },
            { 
                scale: 1, 
                opacity: 1,
                y: 0,
                duration: 0.5,
                stagger: 0.05,
                delay: 0.6,
                ease: "back.out(2)",
            }
        );
        
        // Search bar animation
        gsap.fromTo(
            searchRef.current,
            { 
                opacity: 0,
                width: "80%"
            },
            { 
                opacity: 1,
                width: "100%",
                duration: 0.8,
                delay: 0.8,
                ease: "power2.out"
            }
        );
        
        // Project grid animation
        gsap.fromTo(
            projectsGridRef.current,
            { opacity: 0 },
            { 
                opacity: 1, 
                duration: 0.8,
                delay: 0.9,
                ease: "power2.out" 
            }
        );
        
        // Staggered project cards animation
        gsap.fromTo(
            projectCardsRef.current,
            { 
                y: 80, 
                opacity: 0,
                scale: 0.9
            },
            { 
                y: 0, 
                opacity: 1,
                scale: 1,
                duration: 0.8,
                stagger: 0.1,
                delay: 1,
                ease: "back.out(1.7)",
                clearProps: "all"
            }
        );
        
        // Setup scroll animations for cards that enter viewport later
        projectCardsRef.current.forEach((card, index) => {
            ScrollTrigger.create({
                trigger: card,
                start: "top bottom-=100",
                onEnter: () => {
                    gsap.fromTo(
                        card,
                        { 
                            y: 50, 
                            opacity: 0,
                            scale: 0.9
                        },
                        { 
                            y: 0, 
                            opacity: 1,
                            scale: 1,
                            duration: 0.6,
                            ease: "back.out(1.7)",
                            clearProps: "all"
                        }
                    );
                },
                once: true
            });
        });
    }, [filter, searchQuery]); // Re-run when filters change
    
    // Modal animation
    useEffect(() => {
        if (selectedProject && modalRef.current) {
            gsap.fromTo(
                modalRef.current,
                { 
                    opacity: 0,
                    y: 20
                },
                { 
                    opacity: 1,
                    y: 0,
                    duration: 0.4,
                    ease: "power2.out"
                }
            );
        }
    }, [selectedProject]);

    // Add elements to ref arrays
    const addToFilterRefs = (el) => {
        if (el && !filterButtonsRef.current.includes(el)) {
            filterButtonsRef.current.push(el);
        }
    };
    
    const addToProjectRefs = (el) => {
        if (el && !projectCardsRef.current.includes(el)) {
            projectCardsRef.current.push(el);
        }
    };

    // Get unique categories for filter buttons
    const categories = ['all', ...new Set(projectsData.map(project => project.category))];

    return (
        <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
            <Navbar />
            <main className="flex-grow container mx-auto px-4 py-16 max-w-7xl">
                {/* Hero section with parallax effect */}
                <div 
                    ref={heroRef}
                    className="relative h-80 mb-16 overflow-hidden rounded-3xl shadow-2xl"
                >
                    <div 
                        className="absolute inset-0 bg-gradient-to-r from-[#8f00ff] to-[#ffd700]"
                        style={{
                            backgroundImage: 'url(/code-bg.jpg)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundBlendMode: 'overlay',
                        }}
                    >
                    </div>
                    <div className="absolute inset-0 bg-black/40"></div>
                    <div 
                        ref={heroContentRef}
                        className="absolute inset-0 flex flex-col items-center justify-center text-white p-8"
                    >
                        <h1 className="text-5xl font-bold mb-4 text-center">
                            My Projects Portfolio
                        </h1>
                        <p className="text-xl max-w-2xl text-center">
                            Exploring innovation through code — a showcase of my journey in software development
                        </p>
                    </div>
                </div>
                
                {/* Filters and search */}
                <div 
                    ref={filtersRef}
                    className="flex flex-col md:flex-row justify-between items-center mb-12 gap-4"
                >
                    <div className="flex flex-wrap gap-2">
                        {categories.map(category => (
                            <button 
                                key={category}
                                ref={addToFilterRefs}
                                onClick={() => setFilter(category)}
                                className={`px-4 py-2 rounded-full transition-all duration-300 capitalize ${
                                    filter === category 
                                    ? 'bg-gradient-to-r from-[#8f00ff] to-[#ffd700] text-white shadow-lg' 
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                            >
                                {category === 'all' ? 'All Projects' : category}
                            </button>
                        ))}
                    </div>
                    
                    <div ref={searchRef} className="relative w-full md:w-auto">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search projects..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-[#8f00ff] dark:focus:ring-[#ffd700] focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        />
                    </div>
                </div>
                
                {/* Project grid */}
                <div ref={projectsGridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredProjects.map((project) => (
                        <div
                            key={project.id}
                            ref={addToProjectRefs}
                            className="group bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02]"
                            onClick={() => openModal(project)}
                        >
                            <div className="relative overflow-hidden h-56">
                                <img
                                    src={project.imageUrl}
                                    alt={project.title}
                                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = `https://via.placeholder.com/600x400?text=${project.title.replace(/ /g, '+')}`;
                                    }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-60 transition-opacity duration-300"></div>
                                <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                    <div className="flex space-x-3">
                                        <a 
                                            href={project.demoUrl} 
                                            onClick={(e) => e.stopPropagation()} 
                                            className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-[#8f00ff] shadow-lg hover:bg-[#8f00ff] hover:text-white transition-colors duration-300"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                                        </a>
                                        <a 
                                            href={project.codeUrl} 
                                            onClick={(e) => e.stopPropagation()} 
                                            className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-[#8f00ff] shadow-lg hover:bg-[#8f00ff] hover:text-white transition-colors duration-300"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FontAwesomeIcon icon={faCode} />
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h2 className="text-xl font-semibold mb-3 text-gray-800 dark:text-gray-200 group-hover:text-[#8f00ff] dark:group-hover:text-[#ffd700] transition-colors duration-300">
                                    {project.title}
                                </h2>
                                <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                                    {project.description}
                                </p>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {project.tech.map((tech, index) => (
                                        <span 
                                            key={index}
                                            className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
                                        >
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                                <button className="inline-flex items-center text-[#8f00ff] dark:text-[#ffd700] font-medium hover:text-[#ffd700] dark:hover:text-[#8f00ff] transition-colors duration-300">
                                    View Details 
                                    <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                
                {/* Empty state */}
                {filteredProjects.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <img src="/empty-state.svg" alt="No projects found" className="w-48 h-48 mb-6 opacity-50" />
                        <h3 className="text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No projects found</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6">Try adjusting your filters or search query</p>
                        <button 
                            onClick={() => {setFilter('all'); setSearchQuery('');}}
                            className="px-6 py-2 bg-gradient-to-r from-[#8f00ff] to-[#ffd700] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                            Clear filters
                        </button>
                    </div>
                )}
            </main>
            
            {/* Social links sidebar */}
            <div className="fixed left-6 top-1/2 transform -translate-y-1/2 hidden lg:flex flex-col space-y-7">
                <SocialLink href="#" icon={faTwitter} className="text-gray-400 hover:text-[#8f00ff] dark:hover:text-[#ffd700] transition-colors duration-300" />
                <SocialLink href="#" icon={faLinkedin} className="text-gray-400 hover:text-[#8f00ff] dark:hover:text-[#ffd700] transition-colors duration-300" />
                <SocialLink href="#" icon={faGithub} className="text-gray-400 hover:text-[#8f00ff] dark:hover:text-[#ffd700] transition-colors duration-300" />
                <SocialLink href="#" icon={faMedium} className="text-gray-400 hover:text-[#8f00ff] dark:hover:text-[#ffd700] transition-colors duration-300" />
            </div>
            
            {/* Project detail modal */}
            {selectedProject && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div 
                        ref={modalRef}
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="relative h-72 sm:h-96">
                            <img 
                                src={selectedProject.imageUrl} 
                                alt={selectedProject.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://via.placeholder.com/1200x600?text=${selectedProject.title.replace(/ /g, '+')}`;
                                }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                            <button 
                                onClick={closeModal}
                                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black transition-colors duration-300"
                            >
                                ✕
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 p-6">
                                <h2 className="text-3xl font-bold text-white mb-2">{selectedProject.title}</h2>
                                <div className="flex flex-wrap gap-2">
                                    {selectedProject.tech.map((tech, index) => (
                                        <span 
                                            key={index}
                                            className="px-3 py-1 text-xs bg-white/20 backdrop-blur-sm text-white rounded-full"
                                        >
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <div className="p-6 sm:p-8">
                            <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">About this project</h3>
                            <p className="text-gray-600 dark:text-gray-300 mb-6">{selectedProject.description}</p>
                            
                            <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">Key Features</h3>
                            <ul className="mb-6 space-y-2">
                                {selectedProject.features.map((feature, index) => (
                                    <li key={index} className="flex items-start">
                                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-gradient-to-r from-[#8f00ff] to-[#ffd700] mt-2 mr-3"></div>
                                        <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                            
                            <div className="flex flex-wrap gap-4">
                                <a 
                                    href={selectedProject.demoUrl} 
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-6 py-3 bg-gradient-to-r from-[#8f00ff] to-[#ffd700] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center"
                                >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" />
                                    View Demo
                                </a>
                                <a 
                                    href={selectedProject.codeUrl} 
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-6 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-full shadow hover:shadow-lg hover:border-[#8f00ff] dark:hover:border-[#ffd700] transition-all duration-300 flex items-center"
                                >
                                    <FontAwesomeIcon icon={faCode} className="mr-2" />
                                    View Code
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            <ContactWidget />
            <Footer />
        </div>
    );
}